/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCallback } from "react";
import gatewayFetch from "utilities/gatewayFetch";

interface UseFileFromRefreshUrl {
  getFileFromRefreshUrl: (
    refreshUrl: string,
    fileName: string
  ) => Promise<File>;
}

export const useFileFromRefreshUrl = (): UseFileFromRefreshUrl => {
  const getFileFromRefreshUrl = useCallback(
    async (refreshUrl: string, fileName: string): Promise<File> => {
      const expiringUrlResponse = await gatewayFetch(refreshUrl, {
        method: "GET",
      });

      if (!expiringUrlResponse.ok) {
        throw new Error(
          `Failed to fetch expiring URL: ${expiringUrlResponse.status}`
        );
      }

      const { url } = await expiringUrlResponse.json();

      const fileResponse = await fetch(url);

      if (!fileResponse.ok) {
        throw new Error(
          `Failed to fetch file from expiring URL: ${fileResponse.status}`
        );
      }

      const blob = await fileResponse.blob();
      return new File([blob], fileName, { type: "audio/wav" });
    },
    []
  );

  return { getFileFromRefreshUrl };
};
